import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  GridItem,
  VStack,
  Accordion,
  useColorModeValue,
  useDisclosure,
  SlideFade,
} from '@chakra-ui/react';

import { parseDateString } from 'utils/helpers';
import SectionCard from '../components/SectionCard';
import CollapsibleItemCard from '../components/CollapsibleItemCard';
import AddItemButton from '../components/AddItemButton';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import {
  DriverPersonalInfo,
  DriverLicensing,
  DriverInsuranceDetails,
  DriverOccupation,
  DriverMemberships,
  DriverIncidents,
} from './driver';
import type { Driver, DriversData, Vehicle } from 'api';
import type { Discrepancy } from 'api/quote-marketplace';
import { createEmptyDriver } from '../constants/driverOptions';
import {
  useAnswerCodes,
  useLockAwareAccordion,
  ValidationEntityProvider,
} from '../hooks';

interface DriverStepProps {
  data: DriversData;
  vehicles: Vehicle[];
  effectiveDate: string;
  discrepancies?: Discrepancy[];
  onUpdate: (drivers: Driver[]) => void;
  isUpdating?: boolean;
}

const DriverStep = ({
  data,
  vehicles,
  effectiveDate,
  discrepancies = [],
  onUpdate,
  isUpdating,
}: DriverStepProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const highlightColor = useColorModeValue('yellow.50', 'yellow.900');
  const highlightBorderColor = useColorModeValue('yellow.400', 'yellow.600');
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const incidentsRef = useRef<HTMLDivElement>(null);
  const [driverToDelete, setDriverToDelete] = useState<number | null>(null);

  const [drivers, setDrivers] = useState<Driver[]>(data.drivers || []);
  const [expandedIndex, setExpandedIndex] = useState<number>(() =>
    (data.drivers || []).findIndex((d) => !d.excluded),
  );
  const accordionProps = useLockAwareAccordion({
    itemCount: drivers.length,
    expandedIndex,
    setExpandedIndex,
    allowToggle: true,
  });

  const { getApplicantRelationCode } = useAnswerCodes();
  const applicantCode = getApplicantRelationCode();
  const applicantDriverId =
    drivers.find(
      (d) =>
        !d.excluded && d.personal_info?.relation_to_applicant === applicantCode,
    )?.id ?? null;

  const searchParams = new URLSearchParams(location.search);
  const highlight = searchParams.get('highlight');
  const isIncidentsOnly = highlight === 'incidents';
  const highlightDriverId = highlight?.startsWith('driver-')
    ? parseInt(highlight.split('-')[1], 10)
    : null;
  const highlightSection =
    highlight?.split('-')[2] || (isIncidentsOnly ? 'incidents' : null);

  const firstNonExcludedDriver = drivers.find((d) => !d.excluded);
  const effectiveHighlightDriverId =
    highlightDriverId ?? (isIncidentsOnly ? firstNonExcludedDriver?.id : null);

  useEffect(() => {
    if (data.drivers && data.drivers.length > 0) {
      setDrivers(data.drivers);
    }
  }, [data.drivers]);

  useEffect(() => {
    if (effectiveHighlightDriverId && drivers.length > 0) {
      const driverIndex = drivers.findIndex(
        (d) => d.id === effectiveHighlightDriverId,
      );
      if (driverIndex !== -1) {
        setExpandedIndex(driverIndex);
      }
    }
  }, [effectiveHighlightDriverId, drivers]);

  useEffect(() => {
    if (
      effectiveHighlightDriverId &&
      highlightSection === 'incidents' &&
      incidentsRef.current
    ) {
      const timer = setTimeout(() => {
        incidentsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [effectiveHighlightDriverId, highlightSection, expandedIndex]);

  const clearHighlight = () => {
    if (highlight) {
      navigate(location.pathname, { replace: true });
    }
  };

  const handleDriverChange = (
    index: number,
    field: string,
    value: any,
    nestedField?: string,
  ) => {
    const updated = [...drivers];
    if (nestedField) {
      updated[index] = {
        ...updated[index],
        [field]: {
          ...(updated[index][field as keyof Driver] as object),
          [nestedField]: value,
        },
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setDrivers(updated);
    onUpdate(updated);
  };

  const handleNestedChange = (
    driverIndex: number,
    section: keyof Driver,
    field: string | Record<string, any>,
    value?: any,
    subField?: string,
  ) => {
    setDrivers((prevDrivers) => {
      const updated = [...prevDrivers];
      const driver = updated[driverIndex];
      const sectionData = driver[section] as Record<string, any>;

      if (typeof field === 'object') {
        updated[driverIndex] = {
          ...driver,
          [section]: {
            ...sectionData,
            ...field,
          },
        };
      } else if (subField) {
        updated[driverIndex] = {
          ...driver,
          [section]: {
            ...sectionData,
            [field]: {
              ...(sectionData[field] as object),
              [subField]: value,
            },
          },
        };
      } else {
        updated[driverIndex] = {
          ...driver,
          [section]: {
            ...sectionData,
            [field]: value,
          },
        };
      }

      onUpdate(updated);
      return updated;
    });
  };

  const handleIncidentsChange = (
    driverIndex: number,
    incidentType: string,
    incidents: any[],
  ) => {
    const updated = [...drivers];
    updated[driverIndex] = {
      ...updated[driverIndex],
      incidents: {
        ...updated[driverIndex].incidents,
        [incidentType]: incidents,
      },
    };
    setDrivers(updated);
    onUpdate(updated);
  };

  const handleMembershipsChange = (driverIndex: number, memberships: any[]) => {
    const updated = [...drivers];
    updated[driverIndex] = {
      ...updated[driverIndex],
      memberships,
    };
    setDrivers(updated);
    onUpdate(updated);
  };

  const handleAddDriver = () => {
    const maxId = Math.max(...drivers.map((d) => d.id), 0);
    const newDriver = createEmptyDriver(maxId + 1);
    const updated = [...drivers, newDriver];
    setExpandedIndex(updated.length - 1);
    setDrivers(updated);
    onUpdate(updated);
  };

  const handleDeleteClick = (index: number) => {
    setDriverToDelete(index);
    onDeleteOpen();
  };

  const handleConfirmDelete = () => {
    if (driverToDelete === null) return;

    const updated = drivers.filter((_, i) => i !== driverToDelete);
    if (updated.length === 0) {
      setExpandedIndex(-1);
    } else if (expandedIndex >= updated.length) {
      setExpandedIndex(updated.length - 1);
    }
    setDrivers(updated);
    onUpdate(updated);
    onDeleteClose();
    setDriverToDelete(null);
  };

  const getDriverTitle = (driver: Driver, index: number) => {
    const { first_name, last_name } = driver.personal_info;
    if (first_name || last_name) {
      return `${first_name} ${last_name}`.trim();
    }
    if (driver.personal_info.license_number) {
      return `License: ${driver.personal_info.license_number}`;
    }
    return `Driver ${index + 1}`;
  };

  const getDriverSubtitle = (driver: Driver) => {
    const parts: string[] = [];
    if (driver.licensing.date_of_birth) {
      const age = calculateAge(driver.licensing.date_of_birth);
      if (age) parts.push(`Age ${age}`);
    }
    if (driver.personal_info.license_number) {
      parts.push(driver.personal_info.license_number);
    }
    return parts.join(' • ');
  };

  const getDriverWithMembership = (): {
    driverIndex: number;
    driverName: string;
  } | null => {
    for (let i = 0; i < drivers.length; i++) {
      const driver = drivers[i];
      if (driver.excluded) continue;
      const membership = driver.memberships?.[0];
      if (membership?.membership_group) {
        return {
          driverIndex: i,
          driverName: getDriverTitle(driver, i),
        };
      }
    }
    return null;
  };

  const driverWithMembership = getDriverWithMembership();

  const calculateAge = (dateOfBirth: string | null): number | null => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = parseDateString(dateOfBirth);
    if (!birthDate) return null;
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const renderDriverForm = (driver: Driver, index: number) => {
    return (
      <ValidationEntityProvider
        entity={driver as unknown as Record<string, unknown>}
      >
        <Box>
          <Grid
            templateColumns={{ base: '1fr', '2xl': 'minmax(0, 1fr) 320px' }}
            gap={4}
          >
            {/* Left Column - Driver Details */}
            <GridItem>
              {/* Driver Information - Full Width */}
              <SectionCard title="Driver Information" mb={4}>
                <DriverPersonalInfo
                  driver={driver}
                  index={index}
                  quoteProvince={data.province}
                  applicantDriverId={applicantDriverId}
                  onDriverChange={handleDriverChange}
                  onNestedChange={handleNestedChange}
                />
              </SectionCard>

              {/* Licensing, Insurance Details, Occupation + Memberships */}
              <Grid
                templateColumns={{
                  base: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg:
                    driver.insurance_details?.has_prior_insurance === true
                      ? '3fr 3fr 4fr'
                      : '1fr 1fr',
                }}
                gap={3}
                sx={{
                  transition: 'grid-template-columns 0.3s ease-in-out',
                }}
              >
                <GridItem>
                  <SectionCard title="Licensing" h="100%">
                    <DriverLicensing
                      driver={driver}
                      index={index}
                      quoteProvince={data.province}
                      effectiveDate={effectiveDate}
                      onNestedChange={handleNestedChange}
                    />
                  </SectionCard>
                </GridItem>
                {driver.insurance_details?.has_prior_insurance === true && (
                  <GridItem>
                    <SlideFade in={true} offsetY="10px">
                      <SectionCard title="Insurance Details" h="100%">
                        <DriverInsuranceDetails
                          driver={driver}
                          index={index}
                          effectiveDate={effectiveDate}
                          onNestedChange={handleNestedChange}
                        />
                      </SectionCard>
                    </SlideFade>
                  </GridItem>
                )}
                <GridItem>
                  <VStack spacing={3} align="stretch" h="100%">
                    <SectionCard title="Occupation">
                      <DriverOccupation
                        driver={driver}
                        index={index}
                        onNestedChange={handleNestedChange}
                      />
                    </SectionCard>
                    <SectionCard title="Group Plans / Memberships" flex={1}>
                      <DriverMemberships
                        driver={driver}
                        index={index}
                        isLockedByOtherDriver={
                          driverWithMembership !== null &&
                          driverWithMembership.driverIndex !== index
                        }
                        lockedByDriverName={driverWithMembership?.driverName}
                        onMembershipsChange={handleMembershipsChange}
                      />
                    </SectionCard>
                  </VStack>
                </GridItem>
              </Grid>
            </GridItem>

            {/* Right Column - Incidents */}
            <GridItem>
              <Box
                ref={
                  driver.id === effectiveHighlightDriverId
                    ? incidentsRef
                    : undefined
                }
                borderRadius="md"
                borderWidth={
                  driver.id === effectiveHighlightDriverId &&
                  highlightSection === 'incidents'
                    ? '2px'
                    : undefined
                }
                borderColor={
                  driver.id === effectiveHighlightDriverId &&
                  highlightSection === 'incidents'
                    ? highlightBorderColor
                    : undefined
                }
                bg={
                  driver.id === effectiveHighlightDriverId &&
                  highlightSection === 'incidents'
                    ? highlightColor
                    : undefined
                }
                transition="all 0.3s"
                p={
                  driver.id === effectiveHighlightDriverId &&
                  highlightSection === 'incidents'
                    ? 1
                    : undefined
                }
                onClick={clearHighlight}
              >
                <SectionCard title="Incidents" h="100%">
                  <DriverIncidents
                    driver={driver}
                    index={index}
                    vehicles={vehicles}
                    discrepancies={discrepancies.filter(
                      (d) => d.driver_id === driver.id,
                    )}
                    onIncidentsChange={handleIncidentsChange}
                  />
                </SectionCard>
              </Box>
            </GridItem>
          </Grid>
        </Box>
      </ValidationEntityProvider>
    );
  };

  return (
    <Box>
      <Accordion {...accordionProps}>
        {drivers.map((driver, index) => (
          <CollapsibleItemCard
            key={driver.id}
            title={getDriverTitle(driver, index)}
            subtitle={getDriverSubtitle(driver) || undefined}
            isExcluded={driver.excluded}
            onExcludeChange={(excluded) =>
              handleDriverChange(index, 'excluded', excluded)
            }
            onDelete={() => handleDeleteClick(index)}
          >
            {renderDriverForm(driver, index)}
          </CollapsibleItemCard>
        ))}
      </Accordion>

      <AddItemButton label="Driver" onClick={handleAddDriver} />

      <DeleteConfirmDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleConfirmDelete}
        entityName="Driver"
        itemTitle={
          driverToDelete !== null
            ? getDriverTitle(drivers[driverToDelete], driverToDelete)
            : 'this driver'
        }
      />
    </Box>
  );
};

export default DriverStep;
